import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../../api/client';

interface AutoItem {
  id: number; cbt_item_id: string; site_item_id: string; site_id: string; rule_id: string;
  min_price: number; max_price: number; bounds_amazon_price: number | null;
  markup_source: string; status: string; updated_at: string;
  asin: string | null; title: string | null; ml_price: number | null;
  current_amazon_price: number | null; thumbnail: string | null;
  markup_override: boolean; markup_type: string; markup_value: number;
}
interface EligibleItem {
  item_id: string; seller_sku: string; price: number; amazon_price: number;
  title: string | null; thumbnail: string | null;
  automation_enabled: boolean | null;
}
interface HistoryEntry {
  created_at: string; action: string; new_ml_price: number | null;
  old_amazon_price: number | null; new_amazon_price: number | null;
  markup_type: string | null; markup_value: number | null; reason: string | null;
}

export function AutomationItemsPanel({ sellerId, onRefresh }: { sellerId: number; onRefresh: () => void }) {
  const [activeItems, setActiveItems] = useState<AutoItem[]>([]);
  const [eligible, setEligible] = useState<EligibleItem[]>([]);
  const [showEligible, setShowEligible] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [history, setHistory] = useState<Record<string, HistoryEntry[]>>({});
  const [editMin, setEditMin] = useState<string>('');
  const [editMax, setEditMax] = useState<string>('');
  const [editError, setEditError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [itemsRes, eligibleRes] = await Promise.all([
        api.get<AutoItem[]>(`/pricing-automation/items?seller_id=${sellerId}&status=active`),
        api.get<EligibleItem[]>(`/pricing-automation/eligible?seller_id=${sellerId}`),
      ]);
      if (itemsRes.success && itemsRes.data) setActiveItems(itemsRes.data);
      if (eligibleRes.success && eligibleRes.data) setEligible(eligibleRes.data);
    } finally { setLoading(false); }
  }, [sellerId]);

  useEffect(() => { load(); }, [load]);

  const expandRow = async (cbtItemId: string, item: AutoItem) => {
    if (expanded === cbtItemId) { setExpanded(null); return; }
    setExpanded(cbtItemId);
    setEditMin(String(item.min_price));
    setEditMax(String(item.max_price));
    setEditError(null);
    if (!history[cbtItemId]) {
      const res = await api.get<HistoryEntry[]>(`/pricing-automation/items/${cbtItemId}/history?seller_id=${sellerId}`);
      if (res.success && res.data) setHistory(h => ({ ...h, [cbtItemId]: res.data! }));
    }
  };

  const saveBounds = async (cbtItemId: string) => {
    setSaving(true); setEditError(null);
    try {
      const res = await api.put<any>(`/pricing-automation/items/${cbtItemId}/bounds?seller_id=${sellerId}`, {
        min_price: parseFloat(editMin), max_price: parseFloat(editMax),
      });
      if (!res.success) {
        if ((res as any).error === 'MARGIN_VIOLATION') {
          const d = (res as any).detail;
          setEditError(`min_price debe ser >= $${d.floor.toFixed(2)} (markup floor para Amazon $${d.amazon_price.toFixed(2)})`);
        } else {
          setEditError(res.error || 'Error desconocido');
        }
        return;
      }
      setExpanded(null);
      await load();
    } catch (e: any) { setEditError(e.message); }
    finally { setSaving(false); }
  };

  const disableItem = async (cbtItemId: string) => {
    if (!confirm('Desactivar automation para este item? Se puede reactivar luego.')) return;
    await api.put(`/pricing-automation/items/${cbtItemId}/toggle?seller_id=${sellerId}`, { enabled: false });
    await load(); onRefresh();
  };

  const activateEligible = async (items: EligibleItem[]) => {
    for (const it of items) {
      await api.put(`/pricing-automation/items/${it.item_id}/toggle?seller_id=${sellerId}`, { enabled: true });
    }
    await load(); onRefresh();
  };

  if (loading) return <div className="text-xs text-[var(--color-text-muted)]">Cargando items…</div>;

  return (
    <div className="space-y-3">
      {/* Tabla items activos */}
      <div className="border border-[var(--color-border)] rounded-lg overflow-hidden">
        <div className="px-3 py-2 bg-[var(--color-surface-hover)] text-xs font-semibold text-[var(--color-text)] flex items-center justify-between">
          <span>Items en automation ({activeItems.length})</span>
          <button onClick={load} className="text-[10px] text-[var(--color-text-muted)] hover:text-[var(--color-text)]">Refresh</button>
        </div>
        {activeItems.length === 0 ? (
          <div className="px-3 py-4 text-xs text-[var(--color-text-muted)] text-center">Sin items en automation</div>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-[var(--color-surface-hover)] text-[var(--color-text-secondary)]">
              <tr>
                <th className="px-2 py-1.5 text-left">Item</th>
                <th className="px-2 py-1.5 text-right">Amazon $</th>
                <th className="px-2 py-1.5 text-right">Min $</th>
                <th className="px-2 py-1.5 text-right">Max $</th>
                <th className="px-2 py-1.5 text-left">Rule</th>
                <th className="px-2 py-1.5 text-left">Updated</th>
              </tr>
            </thead>
            <tbody>
              {activeItems.map(it => (
                <React.Fragment key={it.cbt_item_id + '-' + it.site_item_id}>
                  <tr onClick={() => expandRow(it.cbt_item_id, it)}
                      className="border-t border-[var(--color-border-light)] hover:bg-[var(--color-surface-hover)] cursor-pointer">
                    <td className="px-2 py-1.5">
                      <div className="flex items-center gap-2">
                        {it.thumbnail && <img src={it.thumbnail} alt="" className="w-6 h-6 rounded object-cover" />}
                        <div className="min-w-0">
                          <div className="truncate max-w-[200px]" title={it.title || ''}>{it.title || it.cbt_item_id}</div>
                          <div className="text-[10px] text-[var(--color-text-muted)]">{it.asin} · {it.site_id}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-2 py-1.5 text-right">${(it.current_amazon_price ?? it.bounds_amazon_price ?? 0).toFixed(2)}</td>
                    <td className="px-2 py-1.5 text-right">${Number(it.min_price).toFixed(2)}</td>
                    <td className="px-2 py-1.5 text-right">${Number(it.max_price).toFixed(2)}</td>
                    <td className="px-2 py-1.5">{it.rule_id}</td>
                    <td className="px-2 py-1.5 text-[var(--color-text-muted)]">{new Date(it.updated_at).toLocaleString()}</td>
                  </tr>
                  {expanded === it.cbt_item_id && (
                    <tr className="border-t border-[var(--color-border-light)] bg-[var(--color-bg)]">
                      <td colSpan={6} className="px-3 py-3 space-y-3">
                        {/* Override form */}
                        <div>
                          <div className="text-[11px] font-semibold text-[var(--color-text)] mb-1">Override manual de bounds</div>
                          <div className="flex items-end gap-2 flex-wrap">
                            <div>
                              <label className="block text-[10px] text-[var(--color-text-muted)]">Min $</label>
                              <input type="number" step="0.01" value={editMin} onChange={e => setEditMin(e.target.value)}
                                     className="w-24 px-2 py-1 rounded border border-[var(--color-border)] bg-[var(--color-surface)] text-xs" />
                            </div>
                            <div>
                              <label className="block text-[10px] text-[var(--color-text-muted)]">Max $</label>
                              <input type="number" step="0.01" value={editMax} onChange={e => setEditMax(e.target.value)}
                                     className="w-24 px-2 py-1 rounded border border-[var(--color-border)] bg-[var(--color-surface)] text-xs" />
                            </div>
                            <button onClick={() => saveBounds(it.cbt_item_id)} disabled={saving}
                                    className="px-3 py-1 text-xs bg-[var(--color-primary)] text-white rounded disabled:opacity-50">
                              {saving ? 'Guardando...' : 'Guardar'}
                            </button>
                            <button onClick={() => disableItem(it.cbt_item_id)}
                                    className="px-3 py-1 text-xs bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded">
                              Desactivar
                            </button>
                            <span className="text-[10px] text-[var(--color-text-muted)] ml-2">
                              PM recalculará bounds en próximo ciclo
                            </span>
                          </div>
                          {editError && (
                            <div className="mt-2 text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 px-2 py-1 rounded">
                              {editError}
                            </div>
                          )}
                        </div>
                        {/* History */}
                        <div>
                          <div className="text-[11px] font-semibold text-[var(--color-text)] mb-1">Historial de bounds</div>
                          {!history[it.cbt_item_id] ? (
                            <div className="text-[10px] text-[var(--color-text-muted)]">Cargando…</div>
                          ) : history[it.cbt_item_id].length === 0 ? (
                            <div className="text-[10px] text-[var(--color-text-muted)]">Sin historial</div>
                          ) : (
                            <div className="space-y-0.5 max-h-40 overflow-y-auto">
                              {history[it.cbt_item_id].map((h, i) => (
                                <div key={i} className="flex items-center gap-2 text-[10px] text-[var(--color-text-secondary)]">
                                  <span className="text-[var(--color-text-muted)]">{new Date(h.created_at).toLocaleString()}</span>
                                  <span className="font-mono">{h.action}</span>
                                  {h.new_amazon_price != null && <span>amz ${Number(h.new_amazon_price).toFixed(2)}</span>}
                                  {h.new_ml_price != null && <span>ml ${Number(h.new_ml_price).toFixed(2)}</span>}
                                  {h.reason && <span className="text-[var(--color-text-muted)] truncate">{h.reason}</span>}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Panel elegibles */}
      {eligible.length > 0 && (
        <div className="border border-[var(--color-border)] rounded-lg overflow-hidden">
          <button onClick={() => setShowEligible(s => !s)}
                  className="w-full px-3 py-2 bg-[var(--color-surface-hover)] text-xs font-semibold text-[var(--color-text)] flex items-center justify-between">
            <span>Elegibles sin automation ({eligible.length})</span>
            <span className="text-[10px] text-[var(--color-text-muted)]">{showEligible ? '▲' : '▼'}</span>
          </button>
          {showEligible && (
            <div className="max-h-72 overflow-y-auto">
              <table className="w-full text-xs">
                <tbody>
                  {eligible.slice(0, 100).map(e => (
                    <tr key={e.item_id} className="border-t border-[var(--color-border-light)]">
                      <td className="px-2 py-1.5">
                        <div className="flex items-center gap-2">
                          {e.thumbnail && <img src={e.thumbnail} alt="" className="w-6 h-6 rounded object-cover" />}
                          <div className="min-w-0">
                            <div className="truncate max-w-[300px]" title={e.title || ''}>{e.title || e.item_id}</div>
                            <div className="text-[10px] text-[var(--color-text-muted)]">{e.seller_sku} · Amazon ${e.amazon_price?.toFixed(2)}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-2 py-1.5 text-right">
                        <button onClick={() => activateEligible([e])}
                                className="px-2 py-0.5 text-[10px] bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded">
                          Activar
                        </button>
                      </td>
                    </tr>
                  ))}
                  {eligible.length > 100 && (
                    <tr><td colSpan={2} className="px-2 py-1.5 text-[10px] text-[var(--color-text-muted)] text-center">
                      … {eligible.length - 100} más
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
